import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import StyledBackButton from '../../Common/StyledBackButton';
import {
  fetchCoachAvailabilities,
  getRescheduleTimesRequest,
  IBooking,
  updateBooking,
} from '../../../redux/booking/bookingSlice';
import StyledButton from '../../Common/StyledButton';
import CustomDatePicker from './ResheduleBookingDatePicker';
import StyledDropdown from './ResheduleBookingDropDown';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

import { getCoachUnavailableDates } from '../../../utils/utils';

const StyledTypography = styled(Typography)({
  '& span': {
    background: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
});

interface ICancelBookingModalProps {
  handleDismiss: () => void;
  booking: IBooking;
  loadingText: string;
}

function ResheduleBookingModal({ handleDismiss, booking, loadingText }: ICancelBookingModalProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  dayjs.locale(language);
  const minDate = new Date();
  const dispatch = useAppDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs(booking.date).set('hour', 0));
  const [selectedInterval, setSelectedInterval] = useState<Date | null>(null);

  const coachAvailabilities = useAppSelector((state) => state.booking.coachAvailabilities) || [];
  const rescheduleTimes = useAppSelector((state) => state.booking.rescheduleTimes);
  const { updateBookingLoading } = useAppSelector((state) => state.booking);
  const coachUnavailableDays = getCoachUnavailableDates(coachAvailabilities);
  const handleOneTimeChange = (_field: string, value: string) => {
    setSelectedDate(dayjs(value));
  };

  useEffect(() => {
    if (coachId) {
      dispatch(fetchCoachAvailabilities({ coachId }));
    }
  }, [dispatch, coachId]);

  useEffect(() => {
    if (selectedDate && booking?.id) {
      dispatch(
        getRescheduleTimesRequest({
          bookingId: booking.id,
          date: dayjs(selectedDate).date(),
          year: dayjs(selectedDate).year(),
          month: dayjs(selectedDate).month(),
        })
      );
      setSelectedInterval(null);
    }
  }, [dispatch, selectedDate]);

  const handleChangeDropdown = (value: number) => {
    setSelectedInterval(new Date(value * 1000));
  };

  const shouldDisableDate = (date: Dayjs) =>
    coachUnavailableDays.length > 0 &&
    coachUnavailableDays.some((disabledDate) => date.isSame(disabledDate, 'day'));

  const handleReschedule = () => {
    if (selectedInterval && selectedDate && booking.id) {
      dispatch(
        updateBooking({
          bookingId: booking.id,
          date: selectedInterval,
          servicePackageId: booking.servicePackageId,
        })
      );
    }
  };

  return (
    <Grid container flexDirection="column">
      <Grid
        item
        p="36px"
        gap="11px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          background: 'linear-gradient(0deg, rgba(124, 174, 190, 0.2), rgba(124, 174, 190, 0.2))',
        }}
        width="100%"
        borderRadius="10px 10px 0 0"
      >
        <img
          src="/assets/icons/view-bookings-bold.svg"
          alt="booking"
          role="presentation"
          height="56px"
          width="56px"
        />
        <StyledTypography variant="bodyBold" textAlign="center" fontSize="22px">
          <span>{t('bookings.bookingSettings.reshedule')}</span>
        </StyledTypography>
      </Grid>
      <Grid
        display="flex"
        gap="12px"
        justifyContent="center"
        alignItems="center"
        py={4}
        flexDirection="column"
      >
        <Grid item xs={10} display="flex" width="100%">
          <CustomDatePicker
            handleOneTimeChange={handleOneTimeChange}
            minDate={minDate.toISOString().split('T')[0]}
            label={`${t('bookings.bookingSettings.newDate')}`}
            date={selectedDate.toString()}
            shouldDisableDate={shouldDisableDate}
          />
        </Grid>
        <Grid item xs={10} display="flex" width="100%">
          <StyledDropdown
            name="timeZone"
            options={rescheduleTimes}
            value={selectedInterval}
            onChange={(e) => handleChangeDropdown(e.target.value as number)}
            label={`${t('bookings.bookingSettings.selectTime')}`}
          />
        </Grid>
      </Grid>

      <Grid item p="32px 32px 36px 32px" gap={6} display="flex" flexDirection="column">
        <Box display="flex" gap="20px">
          {!updateBookingLoading && (
            <StyledBackButton variant="outlined" fullWidth onClick={handleDismiss}>
              <span>{t('clientTable.deleteClient.dismiss')}</span>
            </StyledBackButton>
          )}
          <StyledButton
            onClick={handleReschedule}
            loading={updateBookingLoading}
            loadingText={loadingText}
            fullWidth
            disabled={!selectedInterval || !selectedDate}
          >
            {t('bookings.bookingSettings.reshedule')}
          </StyledButton>
        </Box>
      </Grid>
    </Grid>
  );
}

export default ResheduleBookingModal;
